// Marca / restaura el estado del teléfono de un cliente.
//
// Estados soportados (campo clientes/{id}.telefonoEstado):
//   - "valido"          (default cuando no existe el campo)
//   - "no_existe"       línea inexistente / fuera de red
//   - "fuera_servicio"  suspendida / dada de baja
//   - "equivocado"      contestan pero no es el cliente
//   - "no_es_titular"   contesta familiar y no quiere dar el dato
//   - "buzon_siempre"   solo cae a buzón, nunca contesta
//
// Cada cambio:
//   - Actualiza el doc clientes/{id} con estado + metadata
//   - Empuja registro al array clientes/{id}.telefonoEstadoHistorial (cap 5)
//   - Registra en colección "auditoria"
//
// Sprint X: marcado de tel inválido por cobranza

import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  serverTimestamp,
  Timestamp,
  collection,
  query,
  where,
  limit as fsLimit,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Firebase/firebaseconfig";
import { registrarAuditoria } from "../helpers/auditoria";

export const TELEFONO_ESTADOS = Object.freeze([
  { value: "valido", label: "Válido", color: "#16a34a" },
  { value: "no_existe", label: "No existe", color: "#dc2626" },
  { value: "fuera_servicio", label: "Fuera de servicio", color: "#dc2626" },
  { value: "equivocado", label: "Número equivocado", color: "#f59e0b" },
  { value: "no_es_titular", label: "No es titular", color: "#f59e0b" },
  { value: "buzon_siempre", label: "Solo buzón", color: "#f59e0b" },
]);

export const ESTADOS_INVALIDOS = new Set([
  "no_existe",
  "fuera_servicio",
  "equivocado",
  "no_es_titular",
  "buzon_siempre",
]);

/**
 * Lee el estado actual de un cliente. Default "valido" si no existe.
 */
export function getEstadoTelefono(cliente) {
  if (!cliente) return "valido";
  return cliente.telefonoEstado || "valido";
}

export function esTelefonoInvalido(cliente) {
  return ESTADOS_INVALIDOS.has(getEstadoTelefono(cliente));
}

export function getLabelEstado(estado) {
  const t = TELEFONO_ESTADOS.find((e) => e.value === estado);
  return t ? t.label : estado;
}

function getActor() {
  const user = getAuth().currentUser;
  return {
    uid: user?.uid || null,
    email: user?.email || "",
    nombre: user?.displayName || user?.email || "Usuario",
  };
}

/**
 * Marca el teléfono del cliente con un nuevo estado.
 * Si el estado es "valido" actúa como restauración.
 */
export async function marcarEstadoTelefono(clienteId, nuevoEstado, motivo = "") {
  if (!clienteId) throw new Error("clienteId requerido");
  const estado = String(nuevoEstado || "").trim();
  if (!TELEFONO_ESTADOS.some((e) => e.value === estado)) {
    throw new Error(`Estado inválido: "${estado}"`);
  }

  const actor = getActor();
  if (!actor.uid) throw new Error("No hay usuario autenticado");

  const ref = doc(db, "clientes", clienteId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Cliente ${clienteId} no existe`);

  const data = snap.data() || {};
  const estadoPrevio = data.telefonoEstado || "valido";
  const telefono = String(data.telefono || "").trim();
  const historialPrevio = Array.isArray(data.telefonoEstadoHistorial)
    ? data.telefonoEstadoHistorial
    : [];

  // Cap histórico a 5 entradas: mantenemos las 4 últimas + la nueva.
  const nuevoHistorial = [
    ...historialPrevio.slice(-4),
    {
      estado,
      estadoPrevio,
      motivo: String(motivo || "").trim(),
      marcadoEn: Timestamp.now(),
      marcadoPor: actor,
    },
  ];

  await updateDoc(ref, {
    telefonoEstado: estado,
    telefonoEstadoMotivo: String(motivo || "").trim(),
    telefonoEstadoMarcadoEn: serverTimestamp(),
    telefonoEstadoMarcadoPor: actor,
    telefonoEstadoHistorial: nuevoHistorial,
  });

  // Audit
  try {
    await registrarAuditoria({
      accion: estado === "valido" ? "telefono.restaurar" : "telefono.marcarInvalido",
      entidadTipo: "cliente",
      entidadId: clienteId,
      detalle: `Telefono ${telefono} → ${getLabelEstado(estado)}${motivo ? " (" + motivo + ")" : ""}`,
      antes: { telefonoEstado: estadoPrevio },
      despues: { telefonoEstado: estado, motivo },
    });
  } catch (e) {
    console.warn("[telefonoService] auditoria fallo:", e?.message);
  }

  return { ok: true, estado, estadoPrevio };
}

/**
 * Lista clientes cuyo teléfono está marcado como inválido (cualquier estado de
 * ESTADOS_INVALIDOS). Útil para el hub de revisión.
 *
 * Firestore "in" acepta hasta 10 valores; nuestra lista tiene 5 → OK.
 *
 * @param {object} [opts]
 * @param {number} [opts.max=500] Cota dura del lado del cliente.
 * @returns {Promise<Array<{id:string, ...cliente, _estadoMeta}>>}
 */
export async function listarClientesConTelefonoInvalido({ max = 500 } = {}) {
  const estados = Array.from(ESTADOS_INVALIDOS);
  const snap = await getDocs(
    query(
      collection(db, "clientes"),
      where("telefonoEstado", "in", estados),
      fsLimit(max)
    )
  );
  return snap.docs.map((d) => {
    const data = d.data() || {};
    const meta = TELEFONO_ESTADOS.find((e) => e.value === data.telefonoEstado);
    return { id: d.id, ...data, _estadoMeta: meta || null };
  });
}
